// utils/url-utils-server.js
// This utility is designed for use inside Next.js Server Components.

/**
 * Updates an existing set of query parameters based on additions/updates
 * and a list of parameters to delete.
 *
 * @param {Object} currentSearchParams - The existing search parameters object (e.g., the prop passed to a Server Component).
 * @param {Object<string, string | number | boolean>} paramsToAddOrUpdate - Key-value pairs of parameters to add or update.
 * @param {string[]} [paramsToDelete=[]] - An array of parameter names to remove.
 * @returns {string} The final query string, starting with '?' if non-empty, e.g., "?author=5&page=1".
 */
export const currentUrlServerSide = (currentSearchParams, paramsToAddOrUpdate, paramsToDelete = []) => {
   // 1. Initialize URLSearchParams with the current parameters.
   // We use currentSearchParams to maintain existing, unrelated parameters.
   // Note: We cast the simple object to URLSearchParams for easy manipulation.
   const searchParams = new URLSearchParams(currentSearchParams);
   
   // 2. Delete specified parameters
   paramsToDelete.forEach(paramKey => {
      if (typeof paramKey === 'string' && paramKey.trim() !== '') {
         searchParams.delete(paramKey);
      }
   });
   
   // 3. Add or update new parameters
   for (const [key, value] of Object.entries(paramsToAddOrUpdate)) {
      const stringValue = String(value);
      
      // Set the parameter only if the value is non-empty/non-whitespace.
      if (stringValue.trim() !== '') {
         searchParams.set(key, stringValue);
      } else {
         // If the intent is to clear the parameter, delete it entirely.
         searchParams.delete(key);
      }
   }
   
   // 4. Final output string
   const finalQueryString = searchParams.toString();
   
   // Return the query string prefixed with '?' if it contains any parameters.
   return finalQueryString ? `?${finalQueryString}` : '';
};